import { Calendar } from '@/_shared/components/calendar/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { useToast } from '@/_shared/hooks/toast.js';
import { newClaimFormRoute } from '@/app/_routes/new-claim.js';
import {
    Button,
    Caption1,
    Card,
    CardHeader,
    Checkbox,
    Combobox,
    Divider,
    Dropdown,
    Field,
    Input,
    Option,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    ProgressBar,
    Spinner,
    Text,
    Textarea,
    tokens,
} from '@fluentui/react-components';
import { CalendarRegular, CheckmarkCircle16Regular, Dismiss24Regular, Document24Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { calculateFileHash } from '@this/app-shared/dist/tools/hash.js';
import { trackException } from '@this/app-shared/dist/tools/insights/track.js';
import { fromIso, toHumanReadable, toIsoDate } from '@this/shared/dist/tools/datetime.js';
import { isValidIban } from '@this/shared/dist/tools/iban.js';
import { de } from 'date-fns/locale';
import { type SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { z } from 'zod';
import { AffectedPersonsTable } from './affected-persons-table.js';
import { CausingPartiesTable } from './causing-parties-table.js';
import { NumberInput } from './number-input.js';
import { useStyles } from './styles.js';

type AffectedPerson = {
    role: 'Mieter' | 'Eigentümer' | 'Sonstiger';
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    buildingUnit: string | null;
    householdInsuranceInsurer: string | null;
    householdInsuranceContractNumber: string | null;
};

type CausingParty = {
    role: 'Mieter' | 'Eigentümer' | 'Sonstiger';
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    buildingUnit: string | null;
    liabilityInsuranceInsurer: string | null;
    liabilityInsuranceContractNumber: string | null;
};

type UploadingFile = {
    hash: string; // Also serves as unique ID
    file: File;
    uploadProgress: number; // 0-100
    status: 'uploading' | 'error';
    error: string | null;
    abortController: AbortController;
};

type UploadedFile = {
    hash: string; // Also serves as unique ID
    name: string;
    mimetype: string;
};

type FormData = {
    claimDate: Date | undefined;
    isClaimDatePreliminary: boolean;
    contract: string;
    policyHolderName: string;
    claimLocation: string;
    objectNumber: string;
    referenceNumber: string;
    contactPerson: string;
    contactPhone: string;
    contactEmail: string;
    claimType: string; // Single-select
    claimCause: string; // Single-select
    claimDescription: string;
    affectedBuildingParts: string;
    floorCoverDamage: string; // Single-select
    expectedClaimAmount: number | null;
    buildingUnitsTotal: number | null;
    buildingUnitsVacant: number | null;
    authoritiesPresent: string[]; // Multi-select
    policeReportFiled: string; // Single-select
    policeProtocolNumber: string;
    policeStation: string;
    inputTaxDeduction: string; // Single-select, empty when left open
    inputTaxDeductionSharePercent: number | null;
    payoutAccount: string; // Single-select, empty when left open
    payoutIban: string;
    payoutAccountHolder: string;
    affectedPersons: AffectedPerson[];
    causingParties: CausingParty[];
    message: string;
    uploadedFiles: UploadedFile[];
};

export function NewClaimForm() {
    const styles = useStyles();
    const tsr = useTsRest();
    const toast = useToast();
    const navigate = useNavigate();
    const { id } = newClaimFormRoute.useParams();

    // Form state
    const [formData, setFormData] = useState<FormData>({
        claimDate: undefined,
        isClaimDatePreliminary: false,
        contract: '',
        policyHolderName: '',
        claimLocation: '',
        objectNumber: '',
        referenceNumber: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        claimType: '', // Single-select
        claimCause: '', // Single-select
        claimDescription: '',
        affectedBuildingParts: '',
        floorCoverDamage: '', // Single-select
        expectedClaimAmount: null,
        buildingUnitsTotal: null,
        buildingUnitsVacant: null,
        authoritiesPresent: [], // Multi-select
        policeReportFiled: '', // Single-select
        policeProtocolNumber: '',
        policeStation: '',
        inputTaxDeduction: '', // Single-select, no preset
        inputTaxDeductionSharePercent: null,
        payoutAccount: '', // Single-select, no preset
        payoutIban: '',
        payoutAccountHolder: '',
        affectedPersons: [],
        causingParties: [],
        message: '',
        uploadedFiles: [],
    });
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [status, setStatus] = useState<'draft' | 'filing' | 'filed' | 'failed'>('draft');
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Load pending claim data
    const { data, isLoading } = tsr.newClaim.getNewClaim.useQuery({
        queryKey: ['pending-claim', id],
        queryData: { params: { id } },
        staleTime: 0, // Data becomes stale immediately
        gcTime: 0, // Discard data immediately
        refetchOnMount: 'always', // Always fetch on mount
        refetchOnWindowFocus: false, // Don't refetch when window gains focus
        refetchOnReconnect: false, // Don't refetch when network reconnects
        initialData: undefined, // Never use initial data
        initialDataUpdatedAt: 0, // Treat any initial data as immediately stale
    });

    // Load form suggestions from claim history
    const { data: suggestions } = tsr.newClaim.getFormSuggestions.useQuery({
        queryKey: ['form-suggestions'],
    });

    // Load contract number proposals (only when claim date is selected)
    const { data: contractNumberProposals } = tsr.newClaim.getContractNumberProposals.useQuery({
        queryKey: ['contract-number-proposals', formData.claimDate],
        queryData: { query: formData.claimDate ? { claimDate: toIsoDate(formData.claimDate) } : {} },
        enabled: !!formData.claimDate,
    });

    // Load claim location proposals based on selected contract (only when both claim date and contract are selected)
    const { data: claimLocationProposals } = tsr.newClaim.getClaimLocationProposals.useQuery({
        queryKey: ['claim-location-proposals', formData.contract, formData.claimDate],
        queryData: {
            query: {
                ...(formData.contract ? { contractNumber: formData.contract } : {}),
                ...(formData.claimDate ? { claimDate: toIsoDate(formData.claimDate) } : {}),
            },
        },
        enabled: !!formData.claimDate && !!formData.contract,
    });

    // Load claim type proposals based on selected contract (only when contract is selected)
    const { data: claimTypeProposals } = tsr.newClaim.getClaimTypeProposals.useQuery({
        queryKey: ['claim-type-proposals', formData.contract],
        queryData: { query: formData.contract ? { contractNumber: formData.contract } : {} },
        enabled: !!formData.contract,
    });

    // Load claim cause proposals based on selected contract and claim type (only when both are selected)
    const { data: claimCauseProposals } = tsr.newClaim.getClaimCauseProposals.useQuery({
        queryKey: ['claim-cause-proposals', formData.contract, formData.claimType],
        queryData: {
            query: {
                ...(formData.contract ? { contractNumber: formData.contract } : {}),
                ...(formData.claimType ? { claimType: formData.claimType } : {}),
            },
        },
        enabled: !!formData.contract && !!formData.claimType,
    });

    const { mutateAsync: updateNewClaim } = tsr.newClaim.updateNewClaim.useMutation();
    const { mutateAsync: getFileUploadUrl } = tsr.newClaim.getFileUploadUrl.useMutation();
    const { mutateAsync: fileNewClaim } = tsr.newClaim.fileNewClaim.useMutation();

    // Helper to update form field
    const updateField = useCallback(
        <K extends keyof FormData>(field: K, value: FormData[K] | SetStateAction<FormData[K]>) => {
            if (status !== 'draft') {
                return;
            }

            setFormData((prev) => ({ ...prev, [field]: typeof value === 'function' ? value(prev[field]) : value }));
            // Clear validation error for this field when user makes changes
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field as string];
                return newErrors;
            });
        },
        [status]
    );

    // Helper to check if a combobox value is custom (not in the options list)
    const isCustomValue = useCallback((value: string, options: string[] | undefined): boolean => {
        if (!value) {
            return false;
        }
        if (!options) {
            return false;
        }
        return !options.includes(value);
    }, []);

    // Minimal validation for saving (only validate format, not required fields)
    const validateForSaving = useCallback((): boolean => {
        const errors: Record<string, string> = {};

        // Only validate format if value is provided
        if (formData.contactEmail) {
            const emailValidation = z.string().email().safeParse(formData.contactEmail);
            if (!emailValidation.success) {
                errors.contactEmail = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            }
        }

        if (formData.payoutAccount === 'Anderes Konto' && formData.payoutIban.trim()) {
            if (!isValidIban(formData.payoutIban)) {
                errors.payoutIban = 'Bitte geben Sie eine gültige IBAN ein';
            }
        }

        if (
            formData.inputTaxDeduction === 'Ja, teilweise' &&
            formData.inputTaxDeductionSharePercent !== null &&
            (formData.inputTaxDeductionSharePercent <= 0 || formData.inputTaxDeductionSharePercent >= 100)
        ) {
            errors.inputTaxDeductionSharePercent = 'Bitte geben Sie einen Anteil zwischen 0 und 100 % ein';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    // Populate form when data loads
    useEffect(() => {
        if (!data?.body) {
            return;
        }

        const claim = data.body;
        setFormData({
            claimDate: claim.claimDate ? fromIso(claim.claimDate) : undefined,
            isClaimDatePreliminary: claim.isClaimDatePreliminary,
            contract: claim.contractNumber ?? '',
            policyHolderName: claim.policyHolderName ?? '',
            claimLocation: claim.claimLocation ?? '',
            objectNumber: claim.customerObjectNumber ?? '',
            referenceNumber: claim.customerReferenceNumber ?? '',
            contactPerson: claim.contactPerson ?? '',
            contactPhone: claim.contactPhone ?? '',
            contactEmail: claim.contactEmail ?? '',
            claimType: claim.claimType ?? '',
            claimCause: claim.claimCause ?? '',
            claimDescription: claim.claimDescription ?? '',
            affectedBuildingParts: claim.affectedBuildingParts ?? '',
            floorCoverDamage: claim.floorCoverDamage ?? '',
            expectedClaimAmount: claim.expectedClaimAmountEur,
            buildingUnitsTotal: claim.buildingUnitsTotal,
            buildingUnitsVacant: claim.buildingUnitsVacant,
            authoritiesPresent: claim.authoritiesPresent as string[],
            policeReportFiled: claim.policeReportFiled ?? '',
            policeProtocolNumber: claim.policeProtocolNumber ?? '',
            policeStation: claim.policeStation ?? '',
            inputTaxDeduction: claim.inputTaxDeduction ?? '',
            inputTaxDeductionSharePercent: claim.inputTaxDeductionSharePercent,
            payoutAccount: claim.payoutAccount ?? '',
            payoutIban: claim.payoutIban ?? '',
            payoutAccountHolder: claim.payoutAccountHolder ?? '',
            affectedPersons: claim.affectedParties,
            causingParties: claim.causingParties,
            message: claim.message ?? '',
            uploadedFiles: claim.files,
        });
        setStatus(claim.status);
    }, [data]);

    const uploadFile = useCallback(
        async (hash: string, file: File, abortController: AbortController) => {
            try {
                // Step 1: Get upload URL
                const urlResponse = await getFileUploadUrl({
                    params: { id },
                    body: {
                        fileHash: hash,
                    },
                });

                if (urlResponse.status !== 200) {
                    throw new Error('Failed to get upload URL');
                }

                const { uploadUrl } = urlResponse.body;

                // Step 2: Upload to blob storage using XMLHttpRequest for progress tracking
                await new Promise<void>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();

                    // Connect abort controller to xhr
                    abortController.signal.addEventListener('abort', () => {
                        xhr.abort();
                    });

                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            const percentComplete = Math.round((e.loaded / e.total) * 100);
                            setUploadingFiles((prev) =>
                                prev.map((f) => (f.hash === hash ? { ...f, uploadProgress: percentComplete } : f))
                            );
                        }
                    });

                    xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            reject(new Error(`Upload failed with status ${xhr.status}`));
                        }
                    });

                    xhr.addEventListener('error', () => {
                        reject(new Error('Upload failed'));
                    });

                    xhr.addEventListener('abort', () => {
                        reject(new Error('Upload cancelled'));
                    });

                    xhr.open('PUT', uploadUrl);
                    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
                    xhr.setRequestHeader('Content-Type', file.type);
                    xhr.send(file);
                });

                // Step 3: Move to uploaded files
                setUploadingFiles((prev) => prev.filter((f) => f.hash !== hash));
                updateField('uploadedFiles', (prev) => {
                    if (prev.some((f) => f.hash === hash)) {
                        return prev;
                    }
                    return [
                        ...prev,
                        {
                            hash,
                            name: file.name,
                            mimetype: file.type,
                        },
                    ];
                });
            } catch (error: any) {
                if (error?.message !== 'Upload cancelled') {
                    trackException(error);
                    setUploadingFiles((prev) =>
                        prev.map((f) =>
                            f.hash === hash
                                ? { ...f, status: 'error' as const, error: error.message || 'Upload failed' }
                                : f
                        )
                    );
                }
            }
        },
        [id, getFileUploadUrl, updateField]
    );

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            // Calculate hashes for all files first
            let acceptedFilesWithHashes = await Promise.all(
                acceptedFiles.map(async (file) => ({
                    file,
                    hash: await calculateFileHash(file),
                }))
            );

            // Remove duplicate hashes
            acceptedFilesWithHashes = acceptedFilesWithHashes.filter(
                (f, index, self) => index === self.findIndex((t) => t.hash === f.hash)
            );

            // Update state
            setUploadingFiles((prev) => {
                // Create uploading file entries
                const newUploadingFiles: UploadingFile[] = acceptedFilesWithHashes
                    .filter((f) => !prev.some((p) => p.hash === f.hash))
                    .map(({ file, hash }) => ({
                        hash,
                        file,
                        uploadProgress: 0,
                        status: 'uploading',
                        error: null,
                        abortController: new AbortController(),
                    }));

                // Start uploads
                newUploadingFiles.forEach(({ hash, file, abortController }) => {
                    uploadFile(hash, file, abortController);
                });

                // Return new state
                return [...prev, ...newUploadingFiles];
            });
        },
        [uploadFile]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        disabled: status !== 'draft',
    });

    const handleCancelUpload = useCallback((hash: string) => {
        setUploadingFiles((prev) => {
            const files = prev.filter((f) => f.hash === hash);
            files.forEach((f) => f.abortController.abort());
            return prev.filter((f) => f.hash !== hash);
        });
    }, []);

    const handleRemoveFile = useCallback(
        (hash: string) => {
            updateField('uploadedFiles', (prev) => prev.filter((f) => f.hash !== hash));
        },
        [updateField]
    );

    const handleSave = useCallback(async () => {
        // Validate before saving
        const isValid = validateForSaving();
        if (!isValid) {
            toast('Validierung fehlgeschlagen', 'Bitte korrigieren Sie die Fehler in den markierten Feldern.', {
                intent: 'error',
                timeout: 3000,
            });
            return false;
        }

        try {
            const result = await updateNewClaim({
                params: { id },
                body: {
                    claimDate: formData.claimDate ? toIsoDate(formData.claimDate) : null,
                    isClaimDatePreliminary: formData.isClaimDatePreliminary,
                    contractNumber: formData.contract || null,
                    policyHolderName: formData.policyHolderName || null,
                    claimLocation: formData.claimLocation || null,
                    customerObjectNumber: formData.objectNumber || null,
                    customerReferenceNumber: formData.referenceNumber || null,
                    contactPerson: formData.contactPerson || null,
                    contactPhone: formData.contactPhone || null,
                    contactEmail: formData.contactEmail || null,
                    claimType: formData.claimType || null,
                    claimCause: formData.claimCause || null,
                    claimDescription: formData.claimDescription || null,
                    affectedBuildingParts: formData.affectedBuildingParts || null,
                    floorCoverDamage: (formData.floorCoverDamage as 'Nein' | 'Ja' | 'Ja (Mietereigentum)') || null,
                    expectedClaimAmountEur: formData.expectedClaimAmount,
                    buildingUnitsTotal: formData.buildingUnitsTotal,
                    buildingUnitsVacant: formData.buildingUnitsVacant,
                    authoritiesPresent: formData.authoritiesPresent as ('Polizei' | 'Feuerwehr')[],
                    policeReportFiled: (formData.policeReportFiled as 'Nein' | 'Ja') || null,
                    policeProtocolNumber: formData.policeProtocolNumber || null,
                    policeStation: formData.policeStation || null,
                    inputTaxDeduction:
                        (formData.inputTaxDeduction as 'Nein' | 'Ja, vollständig' | 'Ja, teilweise') || null,
                    inputTaxDeductionSharePercent:
                        formData.inputTaxDeduction === 'Ja, teilweise' ? formData.inputTaxDeductionSharePercent : null,
                    payoutAccount: (formData.payoutAccount as 'Konto des Beitragszahlers' | 'Anderes Konto') || null,
                    payoutIban: formData.payoutAccount === 'Anderes Konto' ? formData.payoutIban.trim() || null : null,
                    payoutAccountHolder:
                        formData.payoutAccount === 'Anderes Konto' ? formData.payoutAccountHolder.trim() || null : null,
                    affectedParties: formData.affectedPersons,
                    causingParties: formData.causingParties,
                    message: formData.message || null,
                    files: formData.uploadedFiles,
                },
            });

            if (result.status !== 200 || !result.body.success) {
                throw new Error('Failed to save claim');
            }

            toast('Gespeichert', 'Ihre Änderungen wurden erfolgreich gespeichert.', {
                intent: 'success',
                timeout: 3000,
            });

            return true;
        } catch (error) {
            trackException(error);
            toast('Fehler', 'Ihre Änderungen wurden nicht gespeichert.', { intent: 'error', timeout: 3000 });
            return false;
        }
    }, [updateNewClaim, id, formData, toast, validateForSaving]);

    // Check if any files are currently uploading
    const isUploadingFiles = uploadingFiles.length > 0;

    const handleSaveAndSubmit = useCallback(async () => {
        // Step 1: Save the form
        const saved = await handleSave();
        if (!saved) {
            return;
        }

        // Step 2: File the claim (validation happens in backend)
        try {
            const result = await fileNewClaim({
                params: { id },
                body: {},
            });

            if (result.status !== 200) {
                throw new Error('Failed to file claim');
            }

            // Check if validation failed
            if (!result.body.success && result.body.validationErrors) {
                setValidationErrors(result.body.validationErrors);
                toast('Validierung fehlgeschlagen', 'Bitte füllen Sie alle Pflichtfelder korrekt aus.', {
                    intent: 'error',
                    timeout: 5000,
                });
                return;
            }

            if (!result.body.success) {
                throw new Error('Failed to file claim');
            }

            toast('Schaden eingereicht', 'Der Schaden wurde erfolgreich eingereicht.', {
                intent: 'success',
                timeout: 3000,
            });

            // Step 3: Navigate to pending claims list
            navigate({ to: '/pending-claims' });
        } catch (error) {
            trackException(error);
            toast('Fehler', 'Der Schaden konnte nicht eingereicht werden.', { intent: 'error', timeout: 3000 });
        }
    }, [handleSave, fileNewClaim, id, toast, navigate]);

    if (isLoading) {
        return (
            <div className={styles.page}>
                <Spinner size="large" label="Lade Schaden..." />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.form}>
                <h1 className={styles.title}>Schaden anlegen</h1>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 1: Basic claim info | Contact info */}
                <div className={styles.twoColumnRowWithDivider}>
                    {/* Section 1: Date and basic info */}
                    <div className={styles.column}>
                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'end' }}>
                            <Field
                                label="Schadendatum"
                                required
                                validationState={validationErrors.claimDate ? 'error' : undefined}
                                validationMessage={validationErrors.claimDate}
                            >
                                <Popover>
                                    <PopoverTrigger>
                                        <Button appearance="outline" icon={<CalendarRegular />} iconPosition="before">
                                            {formData.claimDate
                                                ? toHumanReadable(formData.claimDate, 'date_short_2digit')
                                                : 'Datum wählen'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverSurface>
                                        <Calendar
                                            locale={de}
                                            mode="single"
                                            selected={formData.claimDate}
                                            onSelect={(date) => updateField('claimDate', date)}
                                            numberOfMonths={1}
                                        />
                                    </PopoverSurface>
                                </Popover>
                            </Field>
                            <Checkbox
                                label="vorläufig"
                                checked={formData.isClaimDatePreliminary}
                                onChange={(_, data) => updateField('isClaimDatePreliminary', data.checked === true)}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                            <Field
                                label="Vertragsnummer"
                                required
                                validationState={validationErrors.contract ? 'error' : undefined}
                                validationMessage={validationErrors.contract}
                                style={{ flex: 1, minWidth: 0 }}
                            >
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.contract}
                                    selectedOptions={formData.contract ? [formData.contract] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('contract', data.optionValue ?? '');
                                            // Find the selected contract to get policy holder name
                                            const selectedContract = contractNumberProposals?.body.contracts.find(
                                                (c) => c.contractNumber === data.optionValue
                                            );
                                            if (selectedContract) {
                                                updateField(
                                                    'policyHolderName',
                                                    selectedContract.policyHolderName ?? ''
                                                );
                                            }
                                        }
                                    }}
                                    onChange={(e) => updateField('contract', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                    className={
                                        isCustomValue(
                                            formData.contract,
                                            contractNumberProposals?.body.contracts.map((c) => c.contractNumber)
                                        )
                                            ? styles.comboboxCustomValue
                                            : undefined
                                    }
                                >
                                    {!formData.claimDate ? (
                                        <Option key="no-date" disabled>
                                            Bitte wählen Sie zuerst das Schadendatum aus
                                        </Option>
                                    ) : !contractNumberProposals ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let contracts = [...contractNumberProposals.body.contracts];

                                                if (
                                                    formData.contract &&
                                                    !contracts.some(
                                                        (contract) =>
                                                            contract.contractNumber.toLowerCase() ===
                                                            formData.contract.toLowerCase()
                                                    )
                                                ) {
                                                    contracts = contracts.filter(
                                                        (contract) =>
                                                            contract.contractNumber
                                                                .toLowerCase()
                                                                .includes(formData.contract.toLowerCase()) ||
                                                            contract.policyHolderName
                                                                ?.toLowerCase()
                                                                .includes(formData.contract.toLowerCase())
                                                    );
                                                    contracts.unshift({
                                                        contractNumber: formData.contract,
                                                        policyHolderName: null,
                                                    });
                                                }

                                                return (
                                                    <>
                                                        {contracts.length > 0 ? (
                                                            contracts.map((contract) => (
                                                                <Option
                                                                    key={contract.contractNumber}
                                                                    value={contract.contractNumber}
                                                                >
                                                                    {contract.policyHolderName
                                                                        ? `${contract.contractNumber} (${contract.policyHolderName})`
                                                                        : contract.contractNumber}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Vertragsnummern
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>

                            <Field label="Versicherungsnehmer" style={{ flex: 1, minWidth: 0 }}>
                                <Input
                                    value={formData.policyHolderName}
                                    onChange={(e) => updateField('policyHolderName', e.target.value)}
                                    style={{ minWidth: 0 }}
                                />
                            </Field>
                        </div>

                        <Field
                            label="Schadenadresse"
                            required
                            validationState={validationErrors.claimLocation ? 'error' : undefined}
                            validationMessage={validationErrors.claimLocation}
                        >
                            <Combobox
                                freeform
                                clearable
                                value={formData.claimLocation}
                                selectedOptions={formData.claimLocation ? [formData.claimLocation] : []}
                                onOptionSelect={(event, data) => {
                                    if (event.type !== 'change') {
                                        updateField('claimLocation', data.optionValue ?? '');
                                    }
                                }}
                                onChange={(e) => updateField('claimLocation', e.target.value)}
                                style={{ minWidth: 0 }}
                                input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                className={
                                    isCustomValue(formData.claimLocation, claimLocationProposals?.body.claimLocations)
                                        ? styles.comboboxCustomValue
                                        : undefined
                                }
                            >
                                {!formData.claimDate || !formData.contract ? (
                                    <Option key="no-selection" disabled>
                                        Bitte wählen Sie zuerst das Schadendatum und die Vertragsnummer aus
                                    </Option>
                                ) : !claimLocationProposals ? (
                                    <Option key="loading" disabled>
                                        Lädt...
                                    </Option>
                                ) : (
                                    <>
                                        {(() => {
                                            let claimLocations = [...claimLocationProposals.body.claimLocations];

                                            if (
                                                formData.claimLocation &&
                                                !claimLocations.some(
                                                    (claimLocation) =>
                                                        claimLocation.toLowerCase() ===
                                                        formData.claimLocation.toLowerCase()
                                                )
                                            ) {
                                                claimLocations = claimLocations.filter((claimLocation) =>
                                                    claimLocation
                                                        .toLowerCase()
                                                        .includes(formData.claimLocation.toLowerCase())
                                                );
                                                claimLocations.unshift(formData.claimLocation);
                                            }

                                            return (
                                                <>
                                                    {claimLocations.length > 0 ? (
                                                        claimLocations.map((value: string) => (
                                                            <Option key={value} value={value}>
                                                                {value}
                                                            </Option>
                                                        ))
                                                    ) : (
                                                        <Option key="no-results" disabled>
                                                            Keine passenden Schadenadressen
                                                        </Option>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </>
                                )}
                            </Combobox>
                        </Field>
                    </div>

                    <Divider vertical className={styles.verticalDivider} />

                    {/* Section 2: Contact and reference */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field label="Ihre Objektnummer">
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.objectNumber}
                                    selectedOptions={formData.objectNumber ? [formData.objectNumber] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('objectNumber', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('objectNumber', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                >
                                    {!suggestions ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let customerObjectNumbers = [...suggestions.body.customerObjectNumbers];

                                                if (
                                                    formData.objectNumber &&
                                                    !customerObjectNumbers.some(
                                                        (objectNumber) =>
                                                            objectNumber.toLowerCase() ===
                                                            formData.objectNumber.toLowerCase()
                                                    )
                                                ) {
                                                    customerObjectNumbers = customerObjectNumbers.filter(
                                                        (objectNumber) =>
                                                            objectNumber
                                                                .toLowerCase()
                                                                .includes(formData.objectNumber.toLowerCase())
                                                    );
                                                    customerObjectNumbers.unshift(formData.objectNumber);
                                                }

                                                return (
                                                    <>
                                                        {customerObjectNumbers.length > 0 ? (
                                                            customerObjectNumbers.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Einträge
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>

                            <Field label="Ihr Aktenzeichen">
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.referenceNumber}
                                    selectedOptions={formData.referenceNumber ? [formData.referenceNumber] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('referenceNumber', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('referenceNumber', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                >
                                    {!suggestions ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let customerReferenceNumbers = [
                                                    ...suggestions.body.customerReferenceNumbers,
                                                ];

                                                if (
                                                    formData.referenceNumber &&
                                                    !customerReferenceNumbers.some(
                                                        (referenceNumber) =>
                                                            referenceNumber.toLowerCase() ===
                                                            formData.referenceNumber.toLowerCase()
                                                    )
                                                ) {
                                                    customerReferenceNumbers = customerReferenceNumbers.filter(
                                                        (referenceNumber) =>
                                                            referenceNumber
                                                                .toLowerCase()
                                                                .includes(formData.referenceNumber.toLowerCase())
                                                    );
                                                    customerReferenceNumbers.unshift(formData.referenceNumber);
                                                }

                                                return (
                                                    <>
                                                        {customerReferenceNumbers.length > 0 ? (
                                                            customerReferenceNumbers.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Einträge
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>
                        </div>

                        <Field
                            label="Ansprechpartner"
                            required
                            validationState={validationErrors.contactPerson ? 'error' : undefined}
                            validationMessage={validationErrors.contactPerson}
                        >
                            <Combobox
                                freeform
                                clearable
                                value={formData.contactPerson}
                                selectedOptions={formData.contactPerson ? [formData.contactPerson] : []}
                                onOptionSelect={(event, data) => {
                                    if (event.type !== 'change') {
                                        updateField('contactPerson', data.optionValue ?? '');
                                    }
                                }}
                                onChange={(e) => updateField('contactPerson', e.target.value)}
                                style={{ minWidth: 0 }}
                                input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                            >
                                {!suggestions ? (
                                    <Option key="loading" disabled>
                                        Lädt...
                                    </Option>
                                ) : (
                                    <>
                                        {(() => {
                                            let contactPersons = [...suggestions.body.contactPersons];

                                            if (
                                                formData.contactPerson &&
                                                !contactPersons.some(
                                                    (contactPerson) =>
                                                        contactPerson.toLowerCase() ===
                                                        formData.contactPerson.toLowerCase()
                                                )
                                            ) {
                                                contactPersons = contactPersons.filter((contactPerson) =>
                                                    contactPerson
                                                        .toLowerCase()
                                                        .includes(formData.contactPerson.toLowerCase())
                                                );
                                                contactPersons.unshift(formData.contactPerson);
                                            }

                                            return (
                                                <>
                                                    {contactPersons.length > 0 ? (
                                                        contactPersons.map((value: string) => (
                                                            <Option key={value} value={value}>
                                                                {value}
                                                            </Option>
                                                        ))
                                                    ) : (
                                                        <Option key="no-results" disabled>
                                                            Keine passenden Einträge
                                                        </Option>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </>
                                )}
                            </Combobox>
                        </Field>

                        <div className={styles.subRow}>
                            <Field
                                label="Telefon"
                                required
                                validationState={validationErrors.contactPhone ? 'error' : undefined}
                                validationMessage={validationErrors.contactPhone}
                            >
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.contactPhone}
                                    selectedOptions={formData.contactPhone ? [formData.contactPhone] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('contactPhone', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('contactPhone', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                >
                                    {!suggestions ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let contactPhones = [...suggestions.body.contactPhones];

                                                if (
                                                    formData.contactPhone &&
                                                    !contactPhones.some(
                                                        (contactPhone) =>
                                                            contactPhone.toLowerCase() ===
                                                            formData.contactPhone.toLowerCase()
                                                    )
                                                ) {
                                                    contactPhones = contactPhones.filter((contactPhone) =>
                                                        contactPhone
                                                            .toLowerCase()
                                                            .includes(formData.contactPhone.toLowerCase())
                                                    );
                                                    contactPhones.unshift(formData.contactPhone);
                                                }

                                                return (
                                                    <>
                                                        {contactPhones.length > 0 ? (
                                                            contactPhones.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Einträge
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>

                            <Field
                                label="E-Mail"
                                required
                                validationState={validationErrors.contactEmail ? 'error' : undefined}
                                validationMessage={validationErrors.contactEmail}
                            >
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.contactEmail}
                                    selectedOptions={formData.contactEmail ? [formData.contactEmail] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('contactEmail', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('contactEmail', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                >
                                    {!suggestions ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let contactEmails = [...suggestions.body.contactEmails];

                                                if (
                                                    formData.contactEmail &&
                                                    !contactEmails.some(
                                                        (contactEmail) =>
                                                            contactEmail.toLowerCase() ===
                                                            formData.contactEmail.toLowerCase()
                                                    )
                                                ) {
                                                    contactEmails = contactEmails.filter((contactEmail) =>
                                                        contactEmail
                                                            .toLowerCase()
                                                            .includes(formData.contactEmail.toLowerCase())
                                                    );
                                                    contactEmails.unshift(formData.contactEmail);
                                                }

                                                return (
                                                    <>
                                                        {contactEmails.length > 0 ? (
                                                            contactEmails.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Einträge
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>
                        </div>
                    </div>
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 2: Claim type details | Building details */}
                <div className={styles.twoColumnRowWithDivider}>
                    {/* Section 3: Claim type details */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field
                                label="Schadenart"
                                required
                                validationState={validationErrors.claimType ? 'error' : undefined}
                                validationMessage={validationErrors.claimType}
                            >
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.claimType}
                                    selectedOptions={formData.claimType ? [formData.claimType] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('claimType', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('claimType', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                    className={
                                        isCustomValue(formData.claimType, claimTypeProposals?.body.claimTypes)
                                            ? styles.comboboxCustomValue
                                            : undefined
                                    }
                                >
                                    {!formData.contract ? (
                                        <Option key="no-contract" disabled>
                                            Bitte wählen Sie zuerst die Vertragsnummer aus
                                        </Option>
                                    ) : !claimTypeProposals ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let claimTypes = [...claimTypeProposals.body.claimTypes];

                                                if (
                                                    formData.claimType &&
                                                    !claimTypes.some(
                                                        (claimType) =>
                                                            claimType.toLowerCase() === formData.claimType.toLowerCase()
                                                    )
                                                ) {
                                                    claimTypes = claimTypes.filter((claimType) =>
                                                        claimType
                                                            .toLowerCase()
                                                            .includes(formData.claimType.toLowerCase())
                                                    );
                                                    claimTypes.unshift(formData.claimType);
                                                }

                                                return (
                                                    <>
                                                        {claimTypes.length > 0 ? (
                                                            claimTypes.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Schadenarten
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>

                            <Field
                                label="Schadenursache"
                                required
                                validationState={validationErrors.claimCause ? 'error' : undefined}
                                validationMessage={validationErrors.claimCause}
                            >
                                <Combobox
                                    freeform
                                    clearable
                                    value={formData.claimCause}
                                    selectedOptions={formData.claimCause ? [formData.claimCause] : []}
                                    onOptionSelect={(event, data) => {
                                        if (event.type !== 'change') {
                                            updateField('claimCause', data.optionValue ?? '');
                                        }
                                    }}
                                    onChange={(e) => updateField('claimCause', e.target.value)}
                                    style={{ minWidth: 0 }}
                                    input={{ style: { minWidth: 0 }, autoComplete: 'off' }}
                                    className={
                                        isCustomValue(formData.claimCause, claimCauseProposals?.body.claimCauses)
                                            ? styles.comboboxCustomValue
                                            : undefined
                                    }
                                >
                                    {!formData.contract || !formData.claimType ? (
                                        <Option key="no-selection" disabled>
                                            Bitte wählen Sie zuerst die Vertragsnummer und die Schadenart aus
                                        </Option>
                                    ) : !claimCauseProposals ? (
                                        <Option key="loading" disabled>
                                            Lädt...
                                        </Option>
                                    ) : (
                                        <>
                                            {(() => {
                                                let claimCauses = [...claimCauseProposals.body.claimCauses];

                                                if (
                                                    formData.claimCause &&
                                                    !claimCauses.some(
                                                        (claimCause) =>
                                                            claimCause.toLowerCase() ===
                                                            formData.claimCause.toLowerCase()
                                                    )
                                                ) {
                                                    claimCauses = claimCauses.filter((claimCause) =>
                                                        claimCause
                                                            .toLowerCase()
                                                            .includes(formData.claimCause.toLowerCase())
                                                    );
                                                    claimCauses.unshift(formData.claimCause);
                                                }

                                                return (
                                                    <>
                                                        {claimCauses.length > 0 ? (
                                                            claimCauses.map((value: string) => (
                                                                <Option key={value} value={value}>
                                                                    {value}
                                                                </Option>
                                                            ))
                                                        ) : (
                                                            <Option key="no-results" disabled>
                                                                Keine passenden Schadenursachen
                                                            </Option>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </Combobox>
                            </Field>
                        </div>

                        <Field
                            label="Schadenhergang und Schadenursache"
                            required
                            validationState={validationErrors.claimDescription ? 'error' : undefined}
                            validationMessage={validationErrors.claimDescription}
                        >
                            <Textarea
                                placeholder="Bitte beschreiben Sie den Schadenhergang und die Ursache des Schadens..."
                                value={formData.claimDescription}
                                onChange={(_, data) => updateField('claimDescription', data.value)}
                                rows={4}
                                style={{ minWidth: 0 }}
                            />
                        </Field>
                    </div>

                    <Divider vertical className={styles.verticalDivider} />

                    {/* Section 4: Building details */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field
                                label="Beschädigung Bodenbeläge"
                                required
                                validationState={validationErrors.floorCoverDamage ? 'error' : undefined}
                                validationMessage={validationErrors.floorCoverDamage}
                            >
                                <Dropdown
                                    value={formData.floorCoverDamage}
                                    selectedOptions={formData.floorCoverDamage ? [formData.floorCoverDamage] : []}
                                    onOptionSelect={(_, data) =>
                                        updateField('floorCoverDamage', data.optionValue ?? '')
                                    }
                                    style={{ minWidth: 0 }}
                                >
                                    <Option value="Nein">Nein</Option>
                                    <Option value="Ja">Ja</Option>
                                    <Option value="Ja (Mietereigentum)">Ja (Mietereigentum)</Option>
                                </Dropdown>
                            </Field>

                            <Field
                                label="Erwartete Schadenhöhe"
                                required
                                validationState={validationErrors.expectedClaimAmount ? 'error' : undefined}
                                validationMessage={validationErrors.expectedClaimAmount}
                            >
                                <NumberInput
                                    value={formData.expectedClaimAmount}
                                    onChange={(value) => updateField('expectedClaimAmount', value)}
                                    suffix=" €"
                                    style={{ minWidth: 0 }}
                                />
                            </Field>
                        </div>

                        <Field
                            label="Welche Gebäudeteile sind betroffen?"
                            required
                            validationState={validationErrors.affectedBuildingParts ? 'error' : undefined}
                            validationMessage={validationErrors.affectedBuildingParts}
                        >
                            <Textarea
                                placeholder="z.B. Keller, Dach, Wände..."
                                value={formData.affectedBuildingParts}
                                onChange={(_, data) => updateField('affectedBuildingParts', data.value)}
                                rows={4}
                                style={{ minWidth: 0 }}
                            />
                        </Field>
                    </div>
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 3: Authorities | Empty */}
                <div className={styles.twoColumnRowWithDivider}>
                    {/* Section 5: Authorities */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field
                                label="Anwesende Behörden"
                                validationState={validationErrors.authoritiesPresent ? 'error' : undefined}
                                validationMessage={validationErrors.authoritiesPresent}
                            >
                                <Dropdown
                                    multiselect
                                    value={formData.authoritiesPresent.join(', ') || 'Keine'}
                                    selectedOptions={formData.authoritiesPresent}
                                    onOptionSelect={(_, data) =>
                                        updateField('authoritiesPresent', data.selectedOptions)
                                    }
                                    style={{ minWidth: 0 }}
                                >
                                    <Option value="Polizei">Polizei</Option>
                                    <Option value="Feuerwehr">Feuerwehr</Option>
                                </Dropdown>
                            </Field>

                            <Field
                                label="Polizeiliche Anzeige"
                                required
                                validationState={validationErrors.policeReportFiled ? 'error' : undefined}
                                validationMessage={validationErrors.policeReportFiled}
                            >
                                <Dropdown
                                    value={formData.policeReportFiled}
                                    selectedOptions={formData.policeReportFiled ? [formData.policeReportFiled] : []}
                                    onOptionSelect={(_, data) =>
                                        updateField('policeReportFiled', data.optionValue ?? '')
                                    }
                                    style={{ minWidth: 0 }}
                                >
                                    <Option value="Nein">Nein</Option>
                                    <Option value="Ja">Ja</Option>
                                </Dropdown>
                            </Field>
                        </div>

                        {formData.policeReportFiled === 'Ja' && (
                            <div className={styles.subRow}>
                                <Field label="Tagebuchnummer">
                                    <Input
                                        value={formData.policeProtocolNumber}
                                        onChange={(_, data) => updateField('policeProtocolNumber', data.value)}
                                        style={{ minWidth: 0 }}
                                    />
                                </Field>
                                <Field label="Dienststelle">
                                    <Input
                                        value={formData.policeStation}
                                        onChange={(_, data) => updateField('policeStation', data.value)}
                                        style={{ minWidth: 0 }}
                                    />
                                </Field>
                            </div>
                        )}
                    </div>

                    <Divider vertical className={styles.verticalDivider} />

                    {/* Section 6: Building units */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field
                                label="Anzahl Einheiten an Schadenadresse"
                                required
                                validationState={validationErrors.buildingUnitsTotal ? 'error' : undefined}
                                validationMessage={validationErrors.buildingUnitsTotal}
                            >
                                <NumberInput
                                    value={formData.buildingUnitsTotal}
                                    onChange={(value) => updateField('buildingUnitsTotal', value)}
                                    integer
                                    style={{ minWidth: 0 }}
                                />
                            </Field>
                            <Field
                                label="davon leer"
                                required
                                validationState={validationErrors.buildingUnitsVacant ? 'error' : undefined}
                                validationMessage={validationErrors.buildingUnitsVacant}
                            >
                                <NumberInput
                                    value={formData.buildingUnitsVacant}
                                    onChange={(value) => updateField('buildingUnitsVacant', value)}
                                    integer
                                    style={{ minWidth: 0 }}
                                />
                            </Field>
                        </div>
                    </div>
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 4: Input tax deduction | Payout account */}
                <div className={styles.twoColumnRowWithDivider}>
                    {/* Section 7: Input tax deduction, optional: an open answer leaves the question to KIM */}
                    <div className={styles.column}>
                        <div className={styles.subRow}>
                            <Field label="Vorsteuerabzug für das Objekt">
                                <Dropdown
                                    clearable
                                    placeholder="Keine Angabe"
                                    value={formData.inputTaxDeduction}
                                    selectedOptions={formData.inputTaxDeduction ? [formData.inputTaxDeduction] : []}
                                    onOptionSelect={(_, data) =>
                                        updateField('inputTaxDeduction', data.optionValue ?? '')
                                    }
                                    style={{ minWidth: 0 }}
                                >
                                    <Option value="Nein">Nein</Option>
                                    <Option value="Ja, vollständig">Ja, vollständig</Option>
                                    <Option value="Ja, teilweise">Ja, teilweise</Option>
                                </Dropdown>
                            </Field>

                            {formData.inputTaxDeduction === 'Ja, teilweise' && (
                                <Field
                                    label="Anteil"
                                    validationState={
                                        validationErrors.inputTaxDeductionSharePercent ? 'error' : undefined
                                    }
                                    validationMessage={validationErrors.inputTaxDeductionSharePercent}
                                >
                                    <NumberInput
                                        value={formData.inputTaxDeductionSharePercent}
                                        onChange={(value) => updateField('inputTaxDeductionSharePercent', value)}
                                        suffix=" %"
                                        style={{ minWidth: 0 }}
                                    />
                                </Field>
                            )}
                        </div>
                    </div>

                    <Divider vertical className={styles.verticalDivider} />

                    {/* Section 8: Payout account, the premium payer's account or an IBAN the filer names */}
                    <div className={styles.column}>
                        <Field label="Auszahlung auf">
                            <Dropdown
                                clearable
                                placeholder="Keine Angabe"
                                value={formData.payoutAccount}
                                selectedOptions={formData.payoutAccount ? [formData.payoutAccount] : []}
                                onOptionSelect={(_, data) => updateField('payoutAccount', data.optionValue ?? '')}
                                style={{ minWidth: 0 }}
                            >
                                <Option value="Konto des Beitragszahlers">Konto des Beitragszahlers</Option>
                                <Option value="Anderes Konto">Anderes Konto</Option>
                            </Dropdown>
                        </Field>

                        {formData.payoutAccount === 'Anderes Konto' && (
                            <div className={styles.subRow}>
                                <Field
                                    label="IBAN"
                                    required
                                    validationState={validationErrors.payoutIban ? 'error' : undefined}
                                    validationMessage={validationErrors.payoutIban}
                                >
                                    <Input
                                        value={formData.payoutIban}
                                        onChange={(_, data) => updateField('payoutIban', data.value)}
                                        style={{ minWidth: 0 }}
                                    />
                                </Field>
                                <Field label="Kontoinhaber">
                                    <Input
                                        value={formData.payoutAccountHolder}
                                        onChange={(_, data) => updateField('payoutAccountHolder', data.value)}
                                        style={{ minWidth: 0 }}
                                    />
                                </Field>
                            </div>
                        )}
                    </div>
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 5: Affected persons - full width */}
                <div className={styles.sectionFullWidth}>
                    <h2 className={styles.sectionTitle}>Betroffene Personen</h2>
                    <AffectedPersonsTable
                        persons={formData.affectedPersons}
                        onChange={(persons) => updateField('affectedPersons', persons)}
                        status={status}
                    />
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 6: Causing parties - full width */}
                <div className={styles.sectionFullWidth}>
                    <h2 className={styles.sectionTitle}>Verursachende Personen</h2>
                    <CausingPartiesTable
                        parties={formData.causingParties}
                        onChange={(parties) => updateField('causingParties', parties)}
                        status={status}
                    />
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Row 7: Documents | Message */}
                <div className={styles.twoColumnRowWithDivider}>
                    <div className={styles.column}>
                        <h2 className={styles.sectionTitle}>Belege und Unterlagen</h2>
                        <div {...getRootProps()} className={isDragActive ? styles.dropzoneActive : styles.dropzone}>
                            <input {...getInputProps()} />
                            <div className={styles.dropzoneContent}>
                                {uploadingFiles.length === 0 && formData.uploadedFiles.length === 0 ? (
                                    <>
                                        <Button appearance="primary" onClick={open} disabled={status !== 'draft'}>
                                            Dateien hochladen
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: tokens.spacingVerticalS,
                                            }}
                                        >
                                            {/* Show uploading files */}
                                            {uploadingFiles.map((file) => (
                                                <Card key={file.hash} size="small">
                                                    <CardHeader
                                                        image={<Document24Regular />}
                                                        header={<Text weight="semibold">{file.file.name}</Text>}
                                                        description={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {file.status === 'uploading' && (
                                                                    <>
                                                                        <Caption1
                                                                            style={{
                                                                                color: tokens.colorNeutralForeground3,
                                                                            }}
                                                                        >
                                                                            Hochladen... {file.uploadProgress}%
                                                                        </Caption1>
                                                                        <ProgressBar
                                                                            value={file.uploadProgress / 100}
                                                                        />
                                                                    </>
                                                                )}
                                                                {file.status === 'error' && (
                                                                    <Caption1
                                                                        style={{
                                                                            color: tokens.colorPaletteRedForeground1,
                                                                        }}
                                                                    >
                                                                        {file.error ?? 'Fehler beim Hochladen'}
                                                                    </Caption1>
                                                                )}
                                                            </div>
                                                        }
                                                        action={
                                                            <Button
                                                                appearance="transparent"
                                                                icon={<Dismiss24Regular />}
                                                                onClick={() => handleCancelUpload(file.hash)}
                                                                disabled={status !== 'draft'}
                                                                aria-label="Abbrechen"
                                                            />
                                                        }
                                                    />
                                                </Card>
                                            ))}
                                            {/* Show uploaded files */}
                                            {formData.uploadedFiles.map((file: UploadedFile) => (
                                                <Card key={file.hash} size="small">
                                                    <CardHeader
                                                        image={
                                                            <CheckmarkCircle16Regular
                                                                style={{ color: tokens.colorPaletteGreenForeground1 }}
                                                            />
                                                        }
                                                        header={<Text weight="semibold">{file.name}</Text>}
                                                        description={
                                                            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                                                Hochgeladen
                                                            </Caption1>
                                                        }
                                                        action={
                                                            <Button
                                                                appearance="transparent"
                                                                icon={<Dismiss24Regular />}
                                                                onClick={() => handleRemoveFile(file.hash)}
                                                                disabled={status !== 'draft'}
                                                                aria-label="Entfernen"
                                                            />
                                                        }
                                                    />
                                                </Card>
                                            ))}
                                        </div>
                                        <Button
                                            appearance="secondary"
                                            onClick={open}
                                            disabled={status !== 'draft'}
                                            style={{ marginTop: tokens.spacingVerticalM }}
                                        >
                                            Weitere Dateien hochladen
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <Divider vertical className={styles.verticalDivider} />

                    <div className={styles.column}>
                        <h2 className={styles.sectionTitle}>Ihre Nachricht an uns</h2>
                        <Textarea
                            value={formData.message}
                            onChange={(_, data) => updateField('message', data.value)}
                            rows={6}
                            style={{ minWidth: 0 }}
                        />
                    </div>
                </div>

                <Divider appearance="strong" className={styles.divider} />

                {/* Action buttons */}
                <div className={styles.actions}>
                    <Button
                        appearance="secondary"
                        onClick={handleSave}
                        disabled={isUploadingFiles || status !== 'draft'}
                    >
                        Nur speichern
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={handleSaveAndSubmit}
                        disabled={isUploadingFiles || status !== 'draft'}
                    >
                        Speichern und einreichen
                    </Button>
                </div>
            </div>
        </div>
    );
}
